/**
 * The "Search or ask Bill" field in the top bar, base (legal) dialect. Only the
 * leaves that name a matter differ in the business dialect.
 */
export const omnibox = {
    fieldLabel: 'Search or ask Bill',
    resultsLabel: 'Search results',
    escKey: 'esc',
    ask: {
        label: 'Ask Bill: {{query}}',
        hint: 'Start a new chat with this question',
        enter: 'Ask',
    },
    groups: {
        goTo: 'Go to',
        clients: 'Clients',
        matters: 'Matters',
        invoices: 'Invoices',
        recentClients: 'Recent clients',
        recentMatters: 'Recent matters',
        recentInvoices: 'Recent invoices',
        activeClients: 'Recently active clients',
        newestMatters: 'Newest matters',
        latestInvoices: 'Latest invoices',
    },
    /**
     * "Go to" suggestions that jump to a page, a settings tab or section, or an
     * accounting report. Labels of pages, settings tabs, accounting tabs and
     * reports come from the screens themselves (`components/layout/nav-items.ts`,
     * `routes/account/settings-tabs.ts`, `routes/ledger/ledger-tabs.ts`,
     * `routes/ledger/report-tabs.ts`), so a rename shows up here too. The
     * `*Keywords` leaves are the extra words someone might type to find that
     * screen, space separated, and are never shown.
     */
    destinations: {
        /** Where a suggestion lives, shown under its name. */
        location: {
            page: 'Page',
            settings: 'Settings',
            accounting: 'Accounting',
            reports: 'Reports',
            trustReports: 'Trust reports',
        },
        /** Label of a report that also exists for the business ledger, e.g. "Trust trial balance". */
        trustReport: 'Trust {{report}}',
        pageKeywords: {
            dashboard: 'home overview chat ask',
            clients: 'client matters contacts people',
            activities: 'time timesheet timer billable log hours work',
            expenses: 'expense disbursements costs receipts spending suppliers',
            invoices: 'invoice billing bills statements credit notes proforma',
            emails: 'email inbox mail messages gmail outlook',
            calendar: 'meetings diary appointments schedule events',
            'bank-accounts': 'bank statements transactions reconcile reconciliation feeds',
            accounting: 'books ledger bookkeeping finance',
            settings: 'preferences options configuration account',
        },
        settingsTabKeywords: {
            practice: 'practice firm business profile details logo name address',
            billing: 'invoices rates increment due date references bank',
            team: 'members users staff invite colleagues roles',
            subscription: 'plan pricing upgrade payment cancel pro',
            profile: 'my account user name phone whatsapp password sign in login',
            notifications: 'alerts email whatsapp bell reminders',
            accounting: 'ledger financial year year-end vat',
            xero: 'integration connect sync bookkeeping',
            privacy: 'data export delete popia download backup',
        },
        ledgerTabKeywords: {
            reports: 'financial statements',
            accounts: 'chart of accounts ledger accounts gl codes',
            journals: 'journal entries postings transactions',
            assets: 'fixed assets depreciation register',
        },
        reportKeywords: {
            'income-statement': 'profit loss p&l revenue expenses statement',
            'balance-sheet': 'assets liabilities equity financial position',
            'trust-creditors': 'trust reconciliation client money lpc rule 54 creditors',
            'trial-balance': 'tb debits credits',
            'cash-flow': 'cash flow statement movements',
            cashbook: 'receipts payments cash book',
            'debtors-age-analysis': 'debtors ageing aged receivables outstanding owed',
            'creditors-age-analysis': 'creditors ageing aged payables suppliers owe',
        },
        /** Sections inside a settings tab, each with an anchor on its screen. */
        sections: {
            businessInformation: {
                label: 'Business information',
                keywords: 'vat number registration company address legal name entity type',
            },
            practiceProfile: {
                keywords: 'logo name display letterhead branding picture',
            },
            billingSettings: {
                label: 'Billing settings',
                keywords: 'time increment rounding hourly rate email billing km kilometres mileage page rate per page defaults',
            },
            dueDate: {
                label: 'Default invoice due date',
                keywords: 'payment terms days overdue',
            },
            matterReference: {
                keywords: 'prefix number numbering file reference',
            },
            invoiceBankAccounts: {
                label: 'Bank accounts on invoices',
                keywords: 'banking details payment details eft account number branch code',
            },
            inviteMembers: {
                label: 'Invite team members',
                keywords: 'add user colleague email role invitation',
            },
            paymentHistory: {
                label: 'Payment history',
                keywords: 'subscription receipts paid card payments',
            },
            whatsappNumber: {
                label: 'WhatsApp number',
                keywords: 'phone mobile cell verify code link',
            },
            signInMethods: {
                label: 'Sign-in methods',
                keywords: 'password login google microsoft change set email',
            },
            notificationChannels: {
                label: 'Notification channels',
                keywords: 'email whatsapp alerts choose how notified',
            },
            deliveryHours: {
                label: 'Delivery hours',
                keywords: 'quiet hours schedule times send notifications',
            },
            yearEndAndVat: {
                label: 'Year-end & VAT',
                keywords: 'vat financial year filing category vat number month',
            },
            csvImportExport: {
                label: 'CSV import and export',
                keywords: 'csv spreadsheet upload download clients matters activities invoices',
            },
            dataExport: {
                label: 'Export your data',
                keywords: 'download backup copy records documents popia json',
            },
            deleteData: {
                label: 'Delete your data',
                keywords: 'delete remove close cancel account erase popia profile',
            },
        },
    },
    noMatches: 'No clients, matters or invoices match "{{query}}". Try asking Bill instead.',
    noMatchesWithoutInvoices: 'No clients or matters match "{{query}}". Try asking Bill instead.',
    searchFailed: 'Search is not available right now. You can still ask Bill.',
    nothingYet: 'Nothing to show yet. Type to search, or ask Bill a question.',
    draftInvoice: 'Draft invoice',
    inactive: 'Inactive',
};
